import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ApprovalLevel } from './inventoryApproval.types';
import { ReactComponent as Clocka } from '../../../assets/SVG/clocka.svg';
import { ReactComponent as CancelOutlinedIcon } from '../../../assets/SVG/close-circlered.svg';
import { ReactComponent as TickCircle } from '../../../assets/SVG/tick-circle.svg';

import {
  approvalFlowCellContainerSx,
  approvalFlowCardSx,
  approvalFlowRoleSx,
  approvalFlowMetaBoxSx,
  approvalFlowLevelSx,
  approvalFlowMandatorySx,
  approvalFlowArrowSx,
  approvalFlowLevelLineSx,
  approvalFlowOverflowBadgeSx,
  approvalFlowInfoBtnSx,
  approvalFlowPopoverCardSx,
} from './InventoryApprovalSx';
import { formatDateTime } from '../../../utils/dateUtils';

// card ~120px + arrow ~24px + gap ~8px
const CARD_WIDTH = 120;
const ARROW_WIDTH = 32;

interface ApprovalFlowCellProps {
  flow: ApprovalLevel[];
}

const LevelCard: React.FC<{ level: ApprovalLevel; cardSx: object }> = ({ level, cardSx }) => {
  const { t: tInventoryApproval } = useTranslation('inventory_approval_config');
  return (
    <Box sx={cardSx}>
      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} width={'100%'}>
        <Typography sx={approvalFlowRoleSx}>{level.role}</Typography>
        {level.status === 'pending' && <Clocka />}
        {level.status === 'rejected' && <CancelOutlinedIcon />}
        {level.status === 'approved' && <TickCircle />}
      </Box>
  
      <Box sx={approvalFlowMetaBoxSx}>
        <Typography sx={approvalFlowLevelSx}>{tInventoryApproval('level')} {level.level}</Typography>
        {level.isMandatory && (
          <>
            <Typography sx={approvalFlowLevelLineSx}>|</Typography>
            <Typography sx={approvalFlowMandatorySx}>{tInventoryApproval('mandatory')}</Typography>
          </>
        )}

      </Box>
      <Box sx={approvalFlowMetaBoxSx}>
        {level.date && (
          <Typography sx={{ ...approvalFlowLevelSx, color: level.status === 'approved' ? '#16A34A' : level.status === 'rejected' ? '#DC2626' : '#505050' }}>
            {formatDateTime(level.date)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ApprovalFlowCell: React.FC<ApprovalFlowCellProps> = ({ flow }) => {
  const { t: tInventoryApproval } = useTranslation('inventory_approval_config');
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxVisible, setMaxVisible] = useState(flow.length);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const calculate = (width: number) => {
      // space needed for all cards
      const totalWidth = flow.length * CARD_WIDTH + (flow.length - 1) * ARROW_WIDTH;
      if (totalWidth <= width) {
        setMaxVisible(flow.length);
        return;
      }
      // reserve space for "+N more" badge (~60px) + info icon (~32px)
      const reserved = 60 + 32 + ARROW_WIDTH;
      let available = width - reserved;
      let count = 0;
      while (count < flow.length) {
        const needed = CARD_WIDTH + (count > 0 ? ARROW_WIDTH : 0);
        if (available >= needed) {
          available -= needed;
          count++;
        } else break;
      }
      setMaxVisible(Math.max(1, count));
    };

    const observer = new ResizeObserver(([entry]) => {
      calculate(entry.contentRect.width);
    });
    observer.observe(el);
    calculate(el.getBoundingClientRect().width);

    return () => observer.disconnect();
  }, [flow]);

  const visible = flow.slice(0, maxVisible);
  const overflow = flow.length - maxVisible;

  return (
    <Box ref={containerRef} sx={approvalFlowCellContainerSx}>
      {visible.map((level, idx) => (
        <React.Fragment key={idx}>
          <LevelCard level={level} cardSx={approvalFlowCardSx} />
          {idx < visible.length - 1 && (
            <Typography sx={approvalFlowArrowSx}>→</Typography>
          )}
        </React.Fragment>
      ))}

      {overflow > 0 && (
        <>
          <Typography sx={approvalFlowArrowSx}>→</Typography>
          <Typography sx={approvalFlowOverflowBadgeSx}>{tInventoryApproval('more', { count: overflow })}</Typography>
        </>
      )}

      {overflow > 0 && (
        <>
          <IconButton
            size="small"
            sx={approvalFlowInfoBtnSx}
            onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
            data-testid="approval-flow-info-btn"
          >
            <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
          </IconButton>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: { sx: { p: 2, borderRadius: '0.75rem', maxWidth: 480 } },
              root: { onClick: (e: React.MouseEvent) => e.stopPropagation() },
            }}
          >
            <Typography sx={{ ...approvalFlowRoleSx, mb: 1.5, fontWeight: 700 }}>{tInventoryApproval('full_approval_flow')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {flow.map((level, idx) => (
                <React.Fragment key={idx}>
                  <LevelCard level={level} cardSx={approvalFlowPopoverCardSx} />
                  {idx < flow.length - 1 && (
                    <Typography sx={approvalFlowArrowSx}>→</Typography>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

export default ApprovalFlowCell;
